import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop'
import { AdminClient, CrmAssignee, LeadStatus } from '@parkupp/core'
import { GlobalVariablesService } from 'src/app/services/global-variables.service'

@Component({
    selector: 'app-parker-board',
    templateUrl: './parker-board.component.html',
    styleUrls: ['./parker-board.component.scss'],
})
export class ParkerBoardComponent implements OnChanges {
    @Input() adminClients: AdminClient[] = []
    @Input() crmAssigneeMap: { [key: string]: CrmAssignee } = {}
    @Input() filterStatusCategory: string = 'active'
    @Output() leadClicked = new EventEmitter<AdminClient>()
    @Output() statusChanged = new EventEmitter<{ client: AdminClient; newStatus: LeadStatus }>()
    @Output() starToggled = new EventEmitter<AdminClient>()

    private static readonly ALL_COLUMN_DEFS: { status: LeadStatus; label: string; icon: string }[] = [
        { status: LeadStatus.NEW, label: 'New', icon: 'fa-solid fa-sparkles' },
        { status: LeadStatus.CONTACT_ATTEMPTED, label: 'Contact Attempted', icon: 'fa-solid fa-phone-slash' },
        { status: LeadStatus.CONTACTED, label: 'Contacted', icon: 'fa-solid fa-phone' },
        { status: LeadStatus.PROSPECTING, label: 'Prospecting', icon: 'fa-solid fa-binoculars' },
        { status: LeadStatus.NEGOTIATING, label: 'Negotiating', icon: 'fa-solid fa-handshake' },
        { status: LeadStatus.CONVERTED, label: 'Converted', icon: 'fa-solid fa-trophy' },
        { status: LeadStatus.CLOSED, label: 'Closed', icon: 'fa-solid fa-xmark-circle' },
    ]

    columns: { status: LeadStatus; label: string; icon: string; clients: AdminClient[] }[] = []
    connectedLists: string[] = []

    constructor(private globalVariablesService: GlobalVariablesService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['filterStatusCategory'] || changes['adminClients']) {
            this.rebuildColumns()
            this.distributeClients()
        }
    }

    private rebuildColumns(): void {
        const pipelineStatuses = [LeadStatus.NEW, LeadStatus.CONTACT_ATTEMPTED, LeadStatus.CONTACTED, LeadStatus.PROSPECTING, LeadStatus.NEGOTIATING]
        let visibleStatuses: LeadStatus[]

        switch (this.filterStatusCategory) {
            case 'closed':
                visibleStatuses = [LeadStatus.CLOSED]
                break
            case 'converted':
                visibleStatuses = [LeadStatus.CONVERTED]
                break
            case 'all':
                visibleStatuses = [...pipelineStatuses, LeadStatus.CONVERTED, LeadStatus.CLOSED]
                break
            default:
                visibleStatuses = pipelineStatuses
                break
        }

        this.columns = visibleStatuses.map((status) => {
            const def = ParkerBoardComponent.ALL_COLUMN_DEFS.find((d) => d.status === status)!
            return { ...def, clients: [] }
        })
    }

    distributeClients(): void {
        const activeStatuses = this.columns.map((c) => c.status)
        this.connectedLists = this.columns.map((c) => this.getColumnId(c.status))

        const filtered = this.adminClients.filter((c) => activeStatuses.includes(c.leadStatus as LeadStatus))

        this.columns.forEach((col) => {
            col.clients = filtered
                .filter((c) => c.leadStatus === col.status)
                .sort((a, b) => {
                    const aTime = a.createdAt?.seconds || 0
                    const bTime = b.createdAt?.seconds || 0
                    return bTime - aTime
                })
        })
    }

    onDrop(event: CdkDragDrop<AdminClient[]>, targetStatus: LeadStatus): void {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex)
        } else {
            const client = event.previousContainer.data[event.previousIndex]
            transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex)
            this.statusChanged.emit({ client, newStatus: targetStatus })
        }
    }

    getAssigneeName(userResponsible: string | undefined): string {
        if (!userResponsible) return ''
        return this.crmAssigneeMap[userResponsible]?.displayName || ''
    }

    getAssigneeColor(userResponsible: string | undefined): string {
        if (!userResponsible) return '#6c757d'
        return this.crmAssigneeMap[userResponsible]?.badgeColor || '#6c757d'
    }

    getLastTouchpointDate(client: AdminClient): Date {
        const latestCommentDate = client.latestComment?.createdAt
        const createdDate = client.createdAt

        let commentTime = 0
        if (latestCommentDate) {
            commentTime = latestCommentDate.seconds ? latestCommentDate.seconds * 1000 : latestCommentDate instanceof Date ? latestCommentDate.getTime() : new Date(latestCommentDate as any).getTime()
        }

        let createdTime = 0
        if (createdDate) {
            createdTime = (createdDate as any).seconds ? (createdDate as any).seconds * 1000 : createdDate instanceof Date ? createdDate.getTime() : new Date(createdDate as any).getTime()
        }

        return new Date(Math.max(commentTime, createdTime) || Date.now())
    }

    getDaysSinceLastTouchpoint(client: AdminClient): number {
        const lastTouch = this.getLastTouchpointDate(client)
        return Math.floor((new Date().getTime() - lastTouch.getTime()) / (1000 * 60 * 60 * 24))
    }

    formatLastTouchpoint(client: AdminClient): string {
        const days = this.getDaysSinceLastTouchpoint(client)
        if (days === 0) return 'Today'
        if (days === 1) return '1 day ago'
        if (days < 7) return `${days} days ago`
        const weeks = Math.floor(days / 7)
        if (weeks === 1) return '1 week ago'
        if (days < 30) return `${weeks} weeks ago`
        const months = Math.floor(days / 30)
        if (months === 1) return '1 month ago'
        if (days < 365) return `${months} months ago`
        const years = Math.floor(days / 365)
        return years === 1 ? '1 year ago' : `${years} years ago`
    }

    getTouchpointColor(client: AdminClient): string {
        const days = this.getDaysSinceLastTouchpoint(client)
        if (days <= 2) return '#10b981'
        if (days <= 7) return '#34d399'
        if (days <= 14) return '#a3e635'
        if (days <= 21) return '#facc15'
        if (days <= 30) return '#f59e0b'
        if (days <= 60) return '#f97316'
        return '#ef4444'
    }

    hasRealCompany(client: AdminClient): boolean {
        return !!client.companyName && client.companyName.trim().toLowerCase() !== 'private'
    }

    getInitials(client: AdminClient): string {
        const first = (client.firstName || '?')[0].toUpperCase()
        const last = (client.lastName || '')[0]?.toUpperCase() || ''
        return first + last
    }

    getInitialColor(client: AdminClient): string {
        const str = (client.firstName || '') + (client.lastName || '')
        const hash = str.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
        const colors = ['#3e8bff', '#4bd181', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#14b8a6', '#8b5cf6']
        return colors[hash % colors.length]
    }

    getAvatarUrl(client: AdminClient): string {
        if ((client as any).gravatarUrl) return (client as any).gravatarUrl
        if (client.profileImage?.originalUrl) return client.profileImage.mediumUrl || client.profileImage.originalUrl
        return ''
    }

    getLeadValueTier(client: AdminClient): 'HIGH' | 'MEDIUM' | 'LOW' {
        return this.globalVariablesService.getLeadValueTier(client.numberOfBays || 0, client.monthsRequired || 0)
    }

    getColumnId(status: LeadStatus): string {
        return 'board-' + status.toLowerCase().replace(/ /g, '-')
    }
}
